// M2 · Your buddy (QM2_Buddy): who they are, how close you are (days together, never a setting), the relationship as
// it really is (buddy, or romantic only when the user asked for it in chat), and the memory list with edit / delete
// (PATCH / DELETE /api/memory/:id, DELETE /api/memory).
import { useState } from 'preact/hooks';
import { C } from '@shared/content/characters';
import { CLOSENESS } from '@shared/rules';
import { useApp, editMemory, deleteMemory, forgetAll, toast } from '../../store';
import { back, go } from '../../router';
import { Screen, ScrollArea, IconButton, Icon } from '../../components';
import { ConfirmSheet } from './ConfirmSheet';
import { ME } from './copy';
import s from './me.module.css';

export function BuddyScreen() {
  const st = useApp((x) => x.state);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [confirm, setConfirm] = useState(false);
  if (!st) return <Screen theme="margo" id="M2" />;
  const ch = st.ch, c = C[ch], b = st.buddy;

  const save = async (id: string, old: string) => {
    const t = draft.trim();
    setEditing(null);
    if (!t || t === old) return;
    if (await editMemory(id, t)) toast('Saved.');
  };
  const del = async (id: string) => { setEditing(null); if (await deleteMemory(id)) toast(ME.forgotten); };
  const wipe = async () => { setConfirm(false); if (await forgetAll()) toast(ME.forgottenAll(c.name)); };

  return (
    <Screen theme={ch} id="M2">
      <ScrollArea class={s.scroll} fadeTop={0}>
        <div class={s.hero}>
          <img src={c.assets.key} alt={c.name} />
          <div class={s.heroFade} aria-hidden="true" />
        </div>
        <div class={`${s.list} ${s.buddyBody}`}>
          <div class={s.nameRow}>
            <h1 class={s.name}><span>{c.name}</span><span class={s.aiTag}>AI</span></h1>
            <p class={s.role}>{c.role}</p>
          </div>

          <section class={s.card} aria-label={`Who ${c.pron} is`}>
            <p class={s.cap}>{`WHO ${c.pron.toUpperCase()} IS`}</p>
            <p class={s.bio}>{c.bio}</p>
          </section>

          <section class={s.card} aria-label="How close you are">
            <p class={s.cap}>HOW CLOSE YOU ARE</p>
            <div class={s.meterRow} role="img" aria-label={`Closeness: ${CLOSENESS[b.closeness]}`}>
              {CLOSENESS.map((n, i) => <span key={n} data-on={i <= b.closeness ? 'true' : 'false'} />)}
            </div>
            <p class={s.closeName}>{CLOSENESS[b.closeness]}</p>
            <p class={s.closeNote}>{ME.closeNote}</p>
            <div class={s.rel}>
              <span class={s.cap}>RELATIONSHIP</span>
              <span class={s.relV}>{ME.relationship[b.relationship]}</span>
            </div>
            {b.relationship === 'romance' && <p class={s.closeNote}>{ME.romanceNote}</p>}
          </section>

          <section class={`${s.card} ${s.mems}`} aria-label={`What ${c.name} remembers`}>
            <p class={s.cap} style={{ margin: '4px 0 2px' }}>{`WHAT ${c.name.toUpperCase()} REMEMBERS`}</p>
            <p class={s.small} style={{ marginBottom: 6 }}>{ME.memSub(c.name)}</p>
            {b.memory.length === 0 && <p class={s.mem}><span class={s.memT} style={{ color: '#B9B2A6' }}>{ME.memEmpty}</span></p>}
            {b.memory.map((m) => (
              <div class={s.mem} key={m.id}>
                {editing === m.id ? (
                  <form style={{ flex: 1, minWidth: 0, display: 'flex', gap: 6 }} onSubmit={(e) => { e.preventDefault(); void save(m.id, m.text); }}>
                    <input
                      class={s.memIn} type="text" aria-label="Edit memory" value={draft} maxLength={160} autoFocus enterKeyHint="done"
                      onInput={(e) => setDraft((e.currentTarget as HTMLInputElement).value)}
                      onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setEditing(null); } }}
                    />
                    <button type="submit" class={s.save}>Save</button>
                  </form>
                ) : (
                  <>
                    <span class={s.memT}>{m.text}</span>
                    <IconButton icon="edit" label={`Edit: ${m.text}`} variant="ghost" size={44} iconSize={18} iconColor="#CFC8BC" onClick={() => { setDraft(m.text); setEditing(m.id); }} />
                    <IconButton icon="trash" label={`Delete: ${m.text}`} variant="ghost" size={44} iconSize={18} iconColor="#CFC8BC" onClick={() => void del(m.id)} />
                  </>
                )}
              </div>
            ))}
            {b.memory.length > 0 && (
              <button type="button" style={{ marginTop: 6, minHeight: 44, color: '#F28B82', fontSize: 14.5, fontWeight: 700 }} onClick={() => setConfirm(true)}>Forget everything</button>
            )}
          </section>

          <button type="button" class={s.switchRow} onClick={() => go('M3')}><span>Switch buddy</span><Icon name="chevron" size={18} class={s.chev} /></button>
          <p class={s.fine}>{ME.aiChar(c.name)}</p>
        </div>
      </ScrollArea>
      <IconButton icon="back" label="Back" class={s.floatBack} onClick={() => back('M1')} />
      <ConfirmSheet open={confirm} title={ME.forgetTitle(c.name)} body={ME.forgetBody} confirm="Forget everything" onConfirm={() => void wipe()} onClose={() => setConfirm(false)} />
    </Screen>
  );
}
